import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./CustomerActivation.css";
import { 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Database,
  Target,
  TrendingUp,
  Activity,
  Mail,
  MessageSquare,
  Monitor,
  Globe,
  Phone,
  CheckCircle2,
  Brain,
  Sparkles,
  UserCheck,
  Bell,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";


// ============================================
// CUSTOMER ACTIVATION VISUALIZATIONS
// ============================================

// Hero Visualization - IRIS Processing
const IRISVisualization = () => {
  return (
    <div className="act-viz-container iris-viz">
      <div className="act-viz-header">
        <span className="act-viz-label">IRIS INTELLIGENCE ENGINE</span>
        <span className="act-viz-badge green">PROCESSING</span>
      </div>
      
      <div className="iris-stats">
        <div className="iris-stat-main">
          <span className="iris-stat-value">1B+</span>
          <span className="iris-stat-label">Consumer Ad Interactions / Month</span>
        </div>
      </div>
      
      <div className="iris-flow">
        <div className="iris-node">
          <Database size={16} />
          <span>First-Party Data</span>
        </div>
        <div className="iris-arrow">→</div>
        <div className="iris-node active">
          <Brain size={16} />
          <span>IRIS AI</span>
        </div>
        <div className="iris-arrow">→</div>
        <div className="iris-node">
          <Target size={16} />
          <span>Activation</span>
        </div>
      </div>
      
      <div className="iris-capabilities">
        <div className="iris-cap">
          <Sparkles size={14} />
          <span>Content Matching</span>
        </div>
        <div className="iris-cap">
          <UserCheck size={14} />
          <span>Offer Matching</span>
        </div>
        <div className="iris-cap">
          <Send size={14} />
          <span>Channel Matching</span>
        </div>
      </div>
    </div>
  );
};

// Customer Journey Visualization
const CustomerJourneyVisualization = () => {
  const journey = [
    { channel: 'SMS', message: 'Hey Beth, as a new homeowner...', response: 'No Response', status: 'inactive' },
    { channel: 'Email', message: 'Products you need at best prices...', response: 'Opens Email', status: 'engaged' },
    { channel: 'Survey', message: 'Quick survey about your new home', response: 'Completes Survey', status: 'converted' },
  ];

  return (
    <div className="act-viz-container journey-viz">
      <div className="act-viz-header">
        <span className="act-viz-label">CUSTOMER JOURNEY EXAMPLE</span>
        <span className="act-viz-badge">BETH - NEW HOMEOWNER</span>
      </div>
      
      <div className="journey-steps">
        {journey.map((step, i) => (
          <div key={i} className={`journey-step ${step.status}`}>
            <div className="step-channel">
              {step.channel === 'SMS' && <MessageSquare size={16} />}
              {step.channel === 'Email' && <Mail size={16} />}
              {step.channel === 'Survey' && <CheckCircle2 size={16} />}
              <span>{step.channel}</span>
            </div>
            <div className="step-message">{step.message}</div>
            <div className={`step-response ${step.status}`}>{step.response}</div>
          </div>
        ))}
      </div>
      
      <div className="journey-result">
        <div className="result-label">Resulting Profile</div>
        <div className="result-tags">
          <span className="tag">New Homeowner</span>
          <span className="tag">Email Responsive</span>
          <span className="tag">Survey Completer</span>
          <span className="tag">High Intent</span>
        </div>
      </div>
    </div>
  );
};

// Targeting Intelligence Visualization
const TargetingVisualization = () => {
  const signals = [
    { signal: 'Behavioral', score: 94, examples: 'Past responses, browsing patterns' },
    { signal: 'Lookalike', score: 87, examples: 'Similar user modeling' },
    { signal: 'Contextual', score: 91, examples: 'Content affinity, timing' },
    { signal: 'Intent', score: 96, examples: 'Purchase signals, engagement' },
  ];

  return (
    <div className="act-viz-container targeting-viz">
      <div className="act-viz-header">
        <span className="act-viz-label">TARGETING INTELLIGENCE</span>
        <span className="act-viz-badge green">AI-POWERED</span>
      </div>
      
      <div className="targeting-signals">
        {signals.map((s, i) => (
          <div key={i} className="signal-row">
            <div className="signal-info">
              <span className="signal-name">{s.signal}</span>
              <span className="signal-examples">{s.examples}</span>
            </div>
            <div className="signal-score">
              <div className="score-bar">
                <div className="score-fill" style={{ width: `${s.score}%` }}></div>
              </div>
              <span className="score-value">{s.score}</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="targeting-footer">
        <Brain size={14} />
        <span>Collaborative filtering + machine learning</span>
      </div>
    </div>
  );
};

// Activation Results Visualization
const ActivationResultsVisualization = () => {
  const actions = [
    { action: 'Click-through', rate: '4.2%', lift: '+156%' },
    { action: 'Form Completion', rate: '12.8%', lift: '+89%' },
    { action: 'Email Sign-up', rate: '34.5%', lift: '+124%' },
    { action: 'Phone Consent', rate: '8.7%', lift: '+67%' },
  ];

  return (
    <div className="act-viz-container results-viz">
      <div className="act-viz-header">
        <span className="act-viz-label">ACTIVATION PERFORMANCE</span>
        <span className="act-viz-badge green">LIVE METRICS</span>
      </div>
      
      <div className="activation-table">
        <div className="activation-header">
          <span>Action Type</span>
          <span>Rate</span>
          <span>vs. Industry</span>
        </div>
        {actions.map((a, i) => (
          <div key={i} className="activation-row">
            <span className="action-name">{a.action}</span>
            <span className="action-rate">{a.rate}</span>
            <span className="action-lift">{a.lift}</span>
          </div>
        ))}
      </div>
      
      <div className="activation-summary">
        <TrendingUp size={14} />
        <span>Average 109% lift across all activation types</span>
      </div>
    </div>
  );
};

// Omnichannel Visualization
const OmnichannelVisualization = () => {
  const channels = [
    { name: 'Web Ads', icon: Monitor, active: true },
    { name: 'Email', icon: Mail, active: true },
    { name: 'Push', icon: Bell, active: true },
    { name: 'SMS', icon: MessageSquare, active: true },
    { name: 'Voice', icon: Phone, active: true },
    { name: 'Social', icon: Globe, active: false },
  ];

  return (
    <div className="act-viz-container omni-viz">
      <div className="act-viz-header">
        <span className="act-viz-label">OMNICHANNEL ORCHESTRATION</span>
        <span className="act-viz-badge">SYNCHRONIZED</span>
      </div>
      
      <div className="omni-channels">
        {channels.map((ch, i) => (
          <div key={i} className={`omni-channel ${ch.active ? 'active' : ''}`}>
            <ch.icon size={20} />
            <span>{ch.name}</span>
          </div>
        ))}
      </div>
      
      <div className="omni-footer">
        <Activity size={14} />
        <span>Right message, right channel, right time</span>
      </div>
    </div>
  );
};

// Hero Section
const ActHero = () => {
  return (
    <section className="act-hero" data-testid="act-hero">
      <div className="act-hero-content">
        <div className="act-hero-text">
          <span className="act-label">CUSTOMER ACTIVATION</span>
          <h1>Intelligent Targeting. Measurable Activation.</h1>
          <p>
            We don't guess. We collect data straight from the source and design 
            precision marketing campaigns that convert. IRIS, our AI platform, 
            delivers the right offer to the right consumer at the right time—
            driving trust, engagement, and measurable ROI.
          </p>
          <Button 
            className="btn-primary-zeta"
            onClick={() => document.getElementById('act-how')?.scrollIntoView({ behavior: 'smooth' })}
          >
            See How It Works
            <ArrowRight size={18} />
          </Button>
        </div>
        <div className="act-hero-visual">
          <IRISVisualization />
        </div>
      </div>
    </section>
  );
};

// Stats Section
const ActStats = () => {
  const stats = [
    { value: '1B+', label: 'Monthly Ad Interactions' },
    { value: '245M+', label: 'Consumer Profiles' },
    { value: '109%', label: 'Avg. Activation Lift' },
    { value: '33%', label: 'Below CPA Targets' },
  ];

  return (
    <section className="act-stats" data-testid="act-stats">
      <div className="act-stats-grid">
        {stats.map((stat, i) => (
          <div key={i} className="act-stat-card">
            <span className="act-stat-value">{stat.value}</span>
            <span className="act-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

// How IRIS Works Section
const ActHowItWorks = () => {
  return (
    <section className="act-how-section" id="act-how" data-testid="act-how">
      <div className="act-how-content">
        <div className="act-section-header">
          <span className="act-section-label">HOW IRIS WORKS</span>
          <h2>AI-Powered Collaborative Filtering</h2>
          <p>
            IRIS uses collaborative filtering to understand what offers resonate 
            with your target audience—identifying likely responses based on 
            individual behavior and lookalike modeling from similar consumers.
          </p>
        </div>
        
        <div className="act-how-grid">
          <div className="act-how-card">
            <div className="how-icon">
              <Database size={24} />
            </div>
            <h3>Content Matching</h3>
            <p>
              Analyze content affinity to determine which messages 
              and creative formats will resonate with each consumer.
            </p>
          </div>
          
          <div className="act-how-card">
            <div className="how-icon">
              <Target size={24} />
            </div>
            <h3>Offer Matching</h3>
            <p>
              Match specific offers to consumers most likely to 
              respond based on real-time behavioral signals.
            </p>
          </div>
          
          <div className="act-how-card">
            <div className="how-icon">
              <Send size={24} />
            </div>
            <h3>Channel Matching</h3>
            <p>
              Determine optimal channels for each individual—
              email, SMS, push, voice, or display—for maximum engagement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Feature Section - Targeting
const ActTargetingSection = () => {
  return (
    <section className="act-feature-section" data-testid="act-targeting">
      <div className="act-feature-wrapper">
        <div className="act-feature-visual">
          <TargetingVisualization />
        </div>
        <div className="act-feature-text">
          <span className="act-eyebrow">INTELLIGENT TARGETING</span>
          <h2>No Wasted Ad Dollars</h2>
          <p className="act-feature-desc">
            Whether you're an insurance company looking for customers who just 
            bought a new car, a financial services firm seeking debt consolidation 
            prospects, or a retailer wanting to engage new homeowners—IRIS 
            identifies and activates your ideal customers.
          </p>
          <div className="act-bullet-list">
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Behavioral signals from 1B+ monthly interactions</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Lookalike modeling from proven converters</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Real-time intent scoring and optimization</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Feature Section - Journey
const ActJourneySection = () => {
  return (
    <section className="act-feature-section reversed" data-testid="act-journey">
      <div className="act-feature-wrapper">
        <div className="act-feature-visual">
          <CustomerJourneyVisualization />
        </div>
        <div className="act-feature-text">
          <span className="act-eyebrow">CUSTOMER JOURNEY</span>
          <h2>IRIS in Action</h2>
          <p className="act-feature-desc">
            See how IRIS builds customer profiles through multi-channel 
            engagement, learning from each interaction to optimize 
            subsequent touchpoints and drive conversion.
          </p>
          <div className="act-bullet-list">
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Multi-touch attribution across all channels</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Progressive profiling from first-party responses</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Automated sequence optimization based on engagement</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Feature Section - Activation
const ActActivationSection = () => {
  return (
    <section className="act-feature-section" data-testid="act-activation">
      <div className="act-feature-wrapper">
        <div className="act-feature-visual">
          <ActivationResultsVisualization />
        </div>
        <div className="act-feature-text">
          <span className="act-eyebrow">DRIVE RESPONSE</span>
          <h2>From Interest to Action</h2>
          <p className="act-feature-desc">
            IRIS was built to drive action. From simple clicks to loan 
            applications, email sign-ups to phone consent—our intelligence 
            converts interest into measurable outcomes.
          </p>
          <div className="act-bullet-list">
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>109% average lift in activation rates</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Trust-based exchanges drive higher quality leads</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Pay-for-performance models available</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Omnichannel Section
const ActOmnichannelSection = () => {
  return (
    <section className="act-feature-section reversed" data-testid="act-omnichannel">
      <div className="act-feature-wrapper">
        <div className="act-feature-visual">
          <OmnichannelVisualization />
        </div>
        <div className="act-feature-text">
          <span className="act-eyebrow">OMNICHANNEL</span>
          <h2>Be Where Your Customers Are</h2>
          <p className="act-feature-desc">
            Different audiences respond in different places and different ways. 
            We orchestrate top-of-funnel acquisition programs utilizing an 
            optimized multi-channel experience to drive performance.
          </p>
          <div className="act-bullet-list">
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Direct response web ads and email</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Push notifications and SMS opt-ins</p>
            </div>
            <div className="act-bullet-item">
              <CheckCircle2 size={18} className="bullet-icon" />
              <p>Phone calls and voice campaigns</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Case Studies Section
const ActCaseStudies = () => {
  const cases = [
    {
      company: 'Consumer Goods Brand',
      challenge: 'Launch a new premium cleaning product targeted at suburban mothers with children in the home.',
      solution: 'IRIS identified mothers matching the ideal profile who, based on survey responses and lookalike modeling, were most likely to try new products.',
      result: 'Thousands of qualified opt-ins, email-driven initial purchases, ongoing loyalty, and substantial top-of-funnel ROI.',
    },
    {
      company: 'Ride-Share Platform',
      challenge: 'Find qualified new drivers (licensed, clean record) quickly and cost-effectively.',
      solution: 'First-party data, survey responses, and machine learning identified likely drivers, serving ads driving them to application pages.',
      result: 'Tens of thousands of new drivers signed up, campaign cost 33% below maximum CPA threshold.',
    },
  ];

  return (
    <section className="act-cases-section" data-testid="act-cases">
      <div className="act-section-header">
        <span className="act-section-label">REAL-WORLD RESULTS</span>
        <h2>IRIS in Action</h2>
      </div>
      
      <div className="act-cases-grid">
        {cases.map((c, i) => (
          <div key={i} className="act-case-card">
            <span className="case-company">{c.company}</span>
            <div className="case-section">
              <strong>Challenge:</strong>
              <p>{c.challenge}</p>
            </div>
            <div className="case-section">
              <strong>Solution:</strong>
              <p>{c.solution}</p>
            </div>
            <div className="case-section result">
              <strong>Result:</strong>
              <p>{c.result}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// FAQ Section
const ActFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "How does IRIS's collaborative filtering work?",
      answer: "IRIS analyzes patterns across our 1B+ monthly ad interactions to understand what offers resonate with specific audience segments. It identifies likely responses based on each individual's past behavior and the behaviors of similar users (lookalike modeling), continuously improving targeting precision."
    },
    {
      question: "What types of activation actions can you drive?",
      answer: "We support the full spectrum: click-throughs to landing pages, form completions, email and SMS sign-ups, survey completions, phone consent, job/loan applications, and purchase conversions. IRIS optimizes for whatever action your campaign specifies."
    },
    {
      question: "How do you ensure targeting accuracy?",
      answer: "Our targeting combines first-party data we collect directly with behavioral signals, contextual matching, and machine learning models. Unlike third-party data, our signals come from direct consumer interactions within our ecosystem, ensuring higher accuracy and recency."
    },
    {
      question: "What industries benefit most from customer activation?",
      answer: "Insurance, financial services, telecom, retail, healthcare, and consumer goods see strong results. Any business with considered purchase decisions or high customer lifetime value benefits from precision targeting and multi-channel activation."
    },
    {
      question: "How is pricing structured?",
      answer: "We offer flexible models including cost-per-lead, cost-per-acquisition, and cost-per-action. Many clients prefer pay-for-performance structures where you only pay when qualified consumers complete specified actions."
    }
  ];

  return (
    <section className="act-faq-section" data-testid="act-faq">
      <h2>Frequently Asked Questions</h2>
      <div className="act-faq-list">
        {faqs.map((faq, index) => (
          <div 
            key={index} 
            className={`act-faq-item ${openIndex === index ? 'open' : ''}`}
          >
            <button 
              className="act-faq-question"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.question}</span>
              {openIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openIndex === index && (
              <div className="act-faq-answer">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// CTA Section
const ActCTA = () => (
  <section className="act-cta-section" id="act-cta" data-testid="act-cta">
    <div className="act-cta-content">
      <h2>What Can We Help You Achieve?</h2>
      <p>
        Let us show you how IRIS's AI-powered targeting and activation 
        can drive measurable results for your customer acquisition goals.
      </p>
      <Button 
        className="btn-primary-zeta"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        Let's Connect
        <ArrowRight size={18} />
      </Button>
    </div>
  </section>
);

// Main Customer Activation Page Component
function CustomerActivationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="act-page" data-testid="customer-activation-page">
        <SEO 
          title="Customer Activation - Predictive Marketing Solutions"
          description="Transform customer engagement with AI-powered predictive marketing. Anticipate customer needs, optimize timing, and deliver personalized experiences that drive conversions."
          keywords="customer activation, predictive marketing, customer engagement, personalization, marketing automation, customer retention, lifecycle marketing, behavioral targeting"
          canonical="/customer-activation"
        />
        <ActHero />
        <ActStats />
        <ActHowItWorks />
        <ActTargetingSection />
        <ActJourneySection />
        <ActActivationSection />
        <ActOmnichannelSection />
        <ActCaseStudies />
        <ActFAQ />
        <ActCTA />
      </div>
    </Layout>
  );
}

export default CustomerActivationPage;
